import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import { normalizeError } from "@/store/helpers/errorHelper";
import { IResidentApplication } from "@/core/data/residentApplication";

export interface ResidentsRegistrationsInfo {
  errors: unknown;
  myResidentApplication: IResidentApplication | null;
}

// eslint-disable-next-line prefer-const
let pendingMyResidentApplicationPromise: Promise<IResidentApplication> | null =
  null;

@Module({ namespaced: true, name: "ResidentsRegistrationsModule" })
export default class ResidentsRegistrationsModule
  extends VuexModule
  implements ResidentsRegistrationsInfo
{
  errors = {};
  myResidentApplication: IResidentApplication | null = null;

  get getError() {
    const [errorName] = Object.keys(this.errors);
    const error = this.errors[errorName];
    return error;
  }

  get getMyResidentApplicationData(): IResidentApplication | null {
    return this.myResidentApplication;
  }

  @Mutation
  [Mutations.SET_ERROR](errorObj) {
    this.errors = normalizeError(errorObj);
  }

  @Mutation
  [Mutations.SET_MY_RESIDENT_APPLICATION](data: IResidentApplication | null) {
    this.myResidentApplication = data;
    if (!data) {
      pendingMyResidentApplicationPromise = null;
    }
  }

  @Action
  [Actions.GET_RESIDENTS_APPLICATIONS](params?: {
    pageIndex: number;
    pageSize: number;
  }) {
    const pageIndex = params?.pageIndex ?? 1;
    const pageSize = params?.pageSize ?? 1000;
    return ApiService.get(
      `/resident_applications?pageIndex=${pageIndex}&pageSize=${pageSize}`
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
        throw response;
      });
  }

  @Action
  [Actions.GET_MY_RESIDENT_APPLICATION]() {
    if (this.myResidentApplication) {
      return this.myResidentApplication;
    }
    if (pendingMyResidentApplicationPromise) {
      return pendingMyResidentApplicationPromise;
    }
    pendingMyResidentApplicationPromise = ApiService.get(
      "/resident_applications/me"
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, data);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
        throw response;
      })
      .finally(() => {
        pendingMyResidentApplicationPromise = null;
      });
    return pendingMyResidentApplicationPromise;
  }

  @Action
  [Actions.GET_RESIDENT_APPLICATION_BY_ID](id) {
    return ApiService.get(`/resident_applications/${id}`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_RESIDENT_APPLICATION_PROFILE_SUMMARY](id) {
    return ApiService.get(`/resident_applications/${id}/summary`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.APPROVE_RESIDENT_APPLICATION](id) {
    return ApiService.patch(`/resident_applications/${id}/approve`, {})
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.REJECT_RESIDENT_APPLICATION](id) {
    return ApiService.patch(`/resident_applications/${id}/reject`, {})
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_RESIDENT_INFORMATION](data) {
    const body = {
      residentLastName: data.residentLastName,
      residentFirstName: data.residentFirstName,
      residentHealthInsuranceNumber: data.residentHealthInsuranceNumber,
      residentHealthInsuranceExpirationDate:
        data.residentHealthInsuranceExpirationDate,
      longTermCareHomeId: data.residentLongTermCareHome,
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/resident_information`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_PRINCIPAL_RESPONDENT_INFORMATION](data) {
    const body = {
      principalRespondentLastName: data.principalRespondentLastName,
      principalRespondentFirstName: data.principalRespondentFirstName,
      principalRespondentPhoneNumber: data.principalRespondentPhoneNumber,
      principalRespondentAddressLine1: data.principalRespondentAddressLine1,
      principalRespondentAddressLine2: data.principalRespondentAddressLine2,
      principalRespondentAddressPostalCode:
        data.principalRespondentAddressPostalCode,
      principalRespondentAddressCity: data.principalRespondentAddressCity,
      principalRespondentAddressState: data.principalRespondentAddressState,
      principalRespondentAddressCountry: data.principalRespondentAddressCountry,
      principalRespondentRelationshipWithResident:
        data.principalRespondentRelationshipWithResident === "Autre"
          ? data.principalRespondentOtherRelationshipWithResident
          : data.principalRespondentRelationshipWithResident,
      principalRespondentRelationshipPriority:
        data.principalRespondentRelationshipPriority,
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/principal_respondent_information`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_RESIDENT_PERSONAL_INFORMATION](data) {
    const body = {
      residentSex: data.residentSex,
      residentDateOfBirth: data.residentDateOfBirth,
      residentLastNameAfterWedding: data.residentLastNameAfterWedding,
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/resident_personal_information`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_RESIDENT_SOCIAL_DATA](data) {
    const body = {
      residentLanguagePreference:
        data.residentLanguage === "Autre"
          ? data.residentOtherLanguage
          : data.residentLanguage,
      residentBirthPlace: data.residentBirthPlace,
      residentFatherLastName: data.residentFatherLastName,
      residentFatherFirstName: data.residentFatherFirstName,
      residentMotherMaidenName: data.residentMotherMaidenName,
      residentMotherFirstName: data.residentMotherFirstName,
      residentSpouseLastName: data.residentSpouseLastName,
      residentSpouseFirstName: data.residentSpouseFirstName,
      residentSpouseAge: data.residentSpouseAge,
      residentCanadianStatus:
        data.residentCanadianStatus === "Autre"
          ? data.residentOtherCanadianStatus
          : data.residentCanadianStatus,
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/resident_social_data`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_MATERIAL_POSSESSION_RESPONDENT_INFORMATION](data) {
    // FormData encode undefined and null as string value, so we want to set an empty string instead
    Object.keys(data).forEach((key) => {
      if (data[key] === undefined || data[key] === null) {
        data[key] = "";
      }
    });
    const formData = new FormData();
    formData.append(
      "materialPossessionRespondentLastName",
      data.materialPossessionRespondentLastName
    );
    formData.append(
      "materialPossessionRespondentFirstName",
      data.materialPossessionRespondentFirstName
    );
    formData.append(
      "materialPossessionRespondentAddressLine1",
      data.materialPossessionRespondentAddressLine1
    );
    formData.append(
      "materialPossessionRespondentAddressLine2",
      data.materialPossessionRespondentAddressLine2
    );
    formData.append(
      "materialPossessionRespondentAddressPostalCode",
      data.materialPossessionRespondentAddressPostalCode
    );
    formData.append(
      "materialPossessionRespondentAddressCity",
      data.materialPossessionRespondentAddressCity
    );
    formData.append(
      "materialPossessionRespondentAddressState",
      data.materialPossessionRespondentAddressState
    );
    formData.append(
      "materialPossessionRespondentAddressCountry",
      data.materialPossessionRespondentAddressCountry
    );
    formData.append(
      "materialPossessionRespondentHomePhoneNumber",
      data.materialPossessionRespondentHomePhoneNumber
    );
    formData.append(
      "materialPossessionRespondentWorkPhoneNumber",
      data.materialPossessionRespondentWorkPhoneNumber
    );
    formData.append(
      "materialPossessionRespondentCellPhoneNumber",
      data.materialPossessionRespondentCellPhoneNumber
    );
    formData.append(
      "materialPossessionRespondentEmail",
      data.materialPossessionRespondentEmail
    );
    data.curatorTypes.forEach((curatorType, index) => {
      formData.append(`curatorTypes[${index}]`, curatorType);
    });
    data.tutorTypes.forEach((tutorType, index) => {
      formData.append(`tutorTypes[${index}]`, tutorType);
    });
    formData.append(
      "materialPossessionRespondentRelationshipWithResident",
      data.materialPossessionRespondentRelationshipWithResident === "Autre"
        ? data.materialPossessionRespondentOtherRelationshipWithResident
        : data.materialPossessionRespondentRelationshipWithResident
    );
    formData.append(
      "materialPossessionRespondentRelationshipPriority",
      data.materialPossessionRespondentRelationshipPriority
    );
    formData.append(
      "ministryCorrespondanceLanguagePreference",
      data.ministryCorrespondanceLanguagePreference
    );
    if (data.proxyFile !== "" && data.proxyFileType != "") {
      formData.append("proxyFile", data.proxyFile, data.proxyFile.name);
      formData.append("proxyFileTypeId", data.proxyFileType);
    }
    if (data.mandateFile !== "" && data.mandateFileType != "") {
      formData.append("mandateFile", data.mandateFile, data.mandateFile.name);
      formData.append("mandateFileTypeId", data.mandateFileType);
    }
    formData.append("representativeLastName", data.representativeLastName);
    formData.append("representativeFirstName", data.representativeFirstName);

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/material_possession_respondent_information`,
      formData
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_CAREGIVER_RESPONDENT_INFORMATION](data) {
    const body = {
      caregiverRespondentLastName: data.caregiverRespondentLastName,
      caregiverRespondentFirstName: data.caregiverRespondentFirstName,
      caregiverRespondentAddressLine1: data.caregiverRespondentAddressLine1,
      caregiverRespondentAddressLine2: data.caregiverRespondentAddressLine2,
      caregiverRespondentAddressPostalCode:
        data.caregiverRespondentAddressPostalCode,
      caregiverRespondentAddressCity: data.caregiverRespondentAddressCity,
      caregiverRespondentAddressState: data.caregiverRespondentAddressState,
      caregiverRespondentAddressCountry: data.caregiverRespondentAddressCountry,
      caregiverRespondentHomePhoneNumber:
        data.caregiverRespondentHomePhoneNumber,
      caregiverRespondentWorkPhoneNumber:
        data.caregiverRespondentWorkPhoneNumber,
      caregiverRespondentCellPhoneNumber:
        data.caregiverRespondentCellPhoneNumber,
      caregiverRespondentEmail: data.caregiverRespondentEmail,
      caregiverRespondentRelationshipWithResident:
        data.caregiverRespondentRelationshipWithResident === "Autre"
          ? data.caregiverRespondentOtherRelationshipWithResident
          : data.caregiverRespondentRelationshipWithResident,
      caregiverRespondentRelationshipPriority:
        data.caregiverRespondentRelationshipPriority,
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/caregiver_respondent_information`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_SIGNIFICANT_PERSON_INFORMATION](data) {
    const body = {
      significantPersonLastName: data.significantPersonLastName,
      significantPersonFirstName: data.significantPersonFirstName,
      significantPersonAddressLine1: data.significantPersonAddressLine1,
      significantPersonAddressLine2: data.significantPersonAddressLine2,
      significantPersonAddressPostalCode:
        data.significantPersonAddressPostalCode,
      significantPersonAddressCity: data.significantPersonAddressCity,
      significantPersonAddressState: data.significantPersonAddressState,
      significantPersonAddressCountry: data.significantPersonAddressCountry,
      significantPersonHomePhoneNumber: data.significantPersonHomePhoneNumber,
      significantPersonWorkPhoneNumber: data.significantPersonWorkPhoneNumber,
      significantPersonCellPhoneNumber: data.significantPersonCellPhoneNumber,
      significantPersonEmail: data.significantPersonEmail,
      significantPersonRelationshipWithResident:
        data.significantPersonRelationshipWithResident === "Autre"
          ? data.significantPersonOtherRelationshipWithResident
          : data.significantPersonRelationshipWithResident,
      significantPersonRelationshipPriority:
        data.significantPersonRelationshipPriority !== "0" &&
        data.significantPersonRelationshipPriority != 0
          ? data.significantPersonRelationshipPriority
          : null,
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/significant_person_information`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_FUNERAL_ARRANGEMENTS](data) {
    const body = {
      hasFuneralArrangements:
        data.hasFuneralArrangement === "Oui" ? true : false,
      funeralHomeName: data.funeralHomeName,
      funeralHomeAddressLine1: data.funeralHomeAddressLine1,
      funeralHomeAddressLine2: data.funeralHomeAddressLine2,
      funeralHomeAddressCity: data.funeralHomeAddressCity,
      funeralHomeAddressState: data.funeralHomeAddressState,
      funeralHomeAddressPostalCode: data.funeralHomeAddressPostalCode,
      funeralHomeAddressCountry: data.funeralHomeAddressCountry,
      funeralHomePhoneNumber: data.funeralHomePhoneNumber,
      residentReligiousBelief: data.residentReligiousBelief,
      funeralHomeContractNumber: data.funeralHomeContractNumber,
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/save_funeral_arrangements`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_BILLING_INFORMATION](data) {
    const body = {
      sendInvoiceByMail: data.billingMethod === "Poste" ? true : false,
      sendInvoiceByEmail: data.billingMethod === "Poste" ? false : true,
      sendInvoiceTo: data.billingRespondent,
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/save_billing_information`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_PICTURE_AUTHORIZATION](data) {
    const body = {
      pictureAuthorizationSignature: {
        fullName: data.pictureAuthorizationSignatoryFullName,
        signedAt: data.pictureAuthorizationSignatureProvidedAt,
        hasConsented: data.pictureAuthorizationGranted,
      },
    };

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/save_picture_authorization`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_PAYMENT_INFORMATION](data) {
    // FormData encode undefined and null as string value, so we want to set an empty string instead
    Object.keys(data).forEach((key) => {
      if (data[key] === undefined || data[key] === null) {
        data[key] = "";
      }
    });
    const formData = new FormData();
    formData.append(
      "AccountHolderSignature.FullName",
      data.accountHolderFullName
    );
    formData.append(
      "AccountHolderSignature.SignedAt",
      data.accountHolderSignatureProvidedAt
    );
    formData.append(
      "AccountHolderSignature.HasConsented",
      data.accountHolderHasConsentedToPreAuthorizedDebit
    );
    if (data.secondaryAccountHolderFullName) {
      formData.append(
        "SecondaryAccountHolderSignature.FullName",
        data.secondaryAccountHolderFullName
      );
      formData.append(
        "SecondaryAccountHolderSignature.SignedAt",
        data.secondaryAccountHolderSignatureProvidedAt
      );
      formData.append(
        "SecondaryAccountHolderSignature.HasConsented",
        data.secondaryAccountHolderHasConsentedToPreAuthorizedDebit
      );
    }

    formData.append(
      "preAuthorizedDebitConsentInitials",
      data.preAuthorizedDebitConsentInitials
    );
    formData.append(
      "VariableDebitConsentInitials",
      data.variableDebitConsentInitials
    );
    if (data.voidChequeFile !== "") {
      formData.append(
        "VoidChequeFile",
        data.voidChequeFile,
        data.voidChequeFile.name
      );
    }

    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/save_payment_information`,
      formData
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SUBMIT_RESIDENT_APPLICATION](data) {
    return ApiService.patch(
      `/resident_applications/${data.residentApplicationId}/submit`,
      {}
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        this.context.commit(Mutations.SET_MY_RESIDENT_APPLICATION, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_RESIDENT_RELATIONSHIP_PRIORITY]() {
    return ApiService.get("/resident_relationship_priority")
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_RESIDENT_SEXES]() {
    return ApiService.get("/resident_sex")
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_RESIDENT_CURATOR_TYPES]() {
    return ApiService.get("/curator_type")
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_RESIDENT_TUTOR_TYPES]() {
    return ApiService.get("/tutor_type")
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_RESIDENT_FILE_TYPES](file_type) {
    return ApiService.get(`/file_types?type=${file_type}`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_LONG_TERM_CARE_HOMES]() {
    return ApiService.get("/long_term_care_homes")
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.DOWNLOAD_MANDATE_FILE](id) {
    return ApiService.downloadFile(
      `/resident_applications/${id}/download_mandate_file`
    )
      .then((response) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return response;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
        throw response;
      });
  }

  @Action
  [Actions.DOWNLOAD_PROXY_FILE](id) {
    return ApiService.downloadFile(
      `/resident_applications/${id}/download_proxy_file`
    )
      .then((response) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return response;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
        throw response;
      });
  }
}
